import { openDB } from 'idb'
import { sessionEnded } from '@/helpers/sessionState.js'

export default class Db {

  static NAME = 'sasagri'
  static VERSION = 3

  static StoreType = {
    IMAGES: 'images',
    DEVICE_IDENTIFIERS: 'deviceIdentifiers'
  }

  static async db() {
    const db = await openDB(this.NAME, this.VERSION, {
      upgrade: this.migrate.bind(this)
    });

    return db
  }

  static async clearAll() {
    const db = await Db.db()
    const tx = db.transaction(Object.values(this.StoreType), 'readwrite')
    for (const storeName of Object.values(this.StoreType)) {
      const store = tx.objectStore(storeName)
      store.clear()
    }
    await tx.done
  }

  static async clear(storeName) {
    const db = await Db.db()
    const tx = db.transaction(storeName, 'readwrite')
    const store = tx.objectStore(storeName)

    try {
      store.clear()
      await tx.done
    }
    catch(e) { Db.handleFailedTransaction(e) }
  }

  // Nothing is written once the session has ended (helpers/session.js): an image still downloading,
  // or a device identifier arriving late, would otherwise outlive the logout that cleared them
  static async add(storeName, object) {
    if (sessionEnded()) return
    const db = await Db.db()
    const tx = db.transaction(storeName, 'readwrite')
    const store = tx.objectStore(storeName)
    
    try {
      store.add(object)
      await tx.done
    }
    catch(e) { Db.handleFailedTransaction(e) }
  }

  static async set(storeName, object) {
    if (sessionEnded()) return
    const db = await Db.db()
    const tx = db.transaction(storeName, 'readwrite')
    const store = tx.objectStore(storeName)
    
    try {
      store.put(object)
      await tx.done
    }
    catch(e) { Db.handleFailedTransaction(e) }
  }

  static async fromKey(storeName, key) {
    const db = await Db.db()
    const tx = db.transaction(storeName)
    const store = tx.objectStore(storeName)
    let result
    
    try {
      result = store.get(key)
      await tx.done
    }
    catch(e) { Db.handleFailedTransaction(e) }

    return result
  }
  
  static handleFailedTransaction(e) {
    throw new Error(`IndexedDB transaction failed: '${e.message}'`, { cause: e })
  }

  static migrate(db, oldVersion, newVersion, _transaction) {
    console.info(`IndexedDB (${this.NAME}): Migrating from v${oldVersion} to v${newVersion}`)

    // Generate a range from oldVersion + 1 to newVersion.
    // E.g. if oldVersion === 2 and newVersion === 6, then the array would be [3,4,5,6].
    // This means we'd be applying migrations from version 3 up to and including version 6.
    let migrationRange = Array.from({ length: newVersion - oldVersion}, (_, index) => index + oldVersion + 1)

    for (let migrationVersion of migrationRange) {
      console.info(`IndexedDB (${this.NAME}): Migrating to v${migrationVersion}`)
      this[`migrateV${migrationVersion}`](db)
    }

    console.info(`IndexedDB (${this.NAME}): Migration complete`)
  }

  static migrateV1(db) {
    const store = db.createObjectStore(this.StoreType.IMAGES, { keyPath: 'uuid' })
    store.createIndex('uploaded', 'uploaded')
  }

  static migrateV2(db) {
    db.createObjectStore(this.StoreType.DEVICE_IDENTIFIERS, { keyPath: 'userId' })
  }

  static migrateV3(db) {
    db.deleteObjectStore(this.StoreType.DEVICE_IDENTIFIERS)
    db.createObjectStore(this.StoreType.DEVICE_IDENTIFIERS, { keyPath: ['userId', 'partnerId'] })
  }
}
